import Magento from './ecommerce-platforms/Magento2';
import Shopify from './ecommerce-platforms/Shopify'; // eslint-disable-line no-unused-vars

const EcommerceApi = {
  /**
   * An object that stores a cache of all requests to retrieve products from the ecommerce API.
   * The reason we're doing this is because a user could click on a category multiple times before the
   * initial request is returned from the server, so by caching the initial request for the product data,
   * we can avoid additional requests
   */
  productRetrievalPromiseCache: {},

  /**
   * Cache of ecommerce product ID. The merchant product ID is the cache key. Prevents multiple API requests to
   * ecommerce platform for the same SKU. This is especially useful for when a user is on a Variable Product page and
   * is selecting different child SKUs, as the compatible products for each of the child products is likely to be
   * similar
   */
  productCache: {},
  variantOptions: [],
  variantSKUsToProductIdMap: [],

  /**
   * Reset object to its initial state. Useful for automated testing.
   */
  reset: function() {
    this.productRetrievalPromiseCache = {};
    this.productCache = {};
    this.variantOptions = [];
  },

  /**
   * Create a map of variant skus to parent product ids. This is required for the shopify e commerce only
   * e.g. if the product variant is x ft Rod material wood - variant id is 12345678 then its parent product id is
   * what we seek to find it in the shopify store  , say product id - 34561234560
   * here this kind of map is created in advance so that we dont need that for every other variant
   */
  getSkuToProductMap: function(compatioCategoryData) {
    let response = [];
    compatioCategoryData.forEach(category => {
      category.products.forEach(productItem => {
        response.push({
          variantId: productItem.merchantProductId,
          productId: productItem.merchantParentProductId,
          compatioScore: productItem.compatioScore,
        });
      });
    });
    return response;
  },

  /**
   * Calls the callback function implemented by the commerce platform-specific JS
   *
   * This method call will trigger the ecommerce platform to load products from its product API.
   * Using callback functions instead of a Promise since we need to support IE11 and we can't count on the ecommerce
   * platform having a Promise polyfill in place.
   *
   * Data must be returned in the following format:
   *
   * [
   *   {
   *    an object with the keys referenced in
   *    @see Magento.convertGraphQLToCompatioFormat
   *   }
   * ]
   *
   * @param merchantProductSkus
   * @returns {Promise<any>}
   */
  getProductsBySkusFromEcommerce: function(
    merchantProductSkus,
    eCommercePlatform
  ) {
    let resolveCallback,
      rejectCallback,
      merchantProductIDs = [];
    const productDataPromise = new Promise((resolve, reject) => {
      resolveCallback = resolve;
      rejectCallback = reject;
    });
    //if the ecommerce if shopify send the parent product id of the variant
    if (eCommercePlatform === 'shopify') {
      merchantProductSkus.forEach(variantId => {
        let productMap = this.variantSKUsToProductIdMap.find(
          element => element.variantId === variantId
        );
        merchantProductIDs.push(productMap.productId);
      });
    } else {
      merchantProductIDs = merchantProductSkus;
    }
    window.compatioConfig.compatibleProducts.callbacks.getProductsBySkus(
      merchantProductIDs,
      // This callback will be called by commerce platform-specific implementations once data has been retrieved from
      // its respective API
      function returnProductDataCallback(productData) {
        resolveCallback(productData);
      },
      function errorCallback() {
        rejectCallback();
      }
    );
    return productDataPromise;
  },

  getProductsBySkusFromEcommerceXrs: function(
    merchantProductSkus,
    eCommercePlatform
  ) {
    let resolveCallback,
      rejectCallback,
      merchantProductIDs = [];
    const productDataPromise = new Promise((resolve, reject) => {
      resolveCallback = resolve;
      rejectCallback = reject;
    });
    //if the ecommerce if shopify send the parent product id of the variant
    if (eCommercePlatform === 'shopify') {
      merchantProductSkus.forEach(variantId => {
        let productMap = this.variantSKUsToProductIdMap.find(
          element => element.variantId === variantId
        );
        merchantProductIDs.push(productMap.productId);
      });
    } else {
      merchantProductIDs = merchantProductSkus;
    }
    window.compatioConfig.compatibleProducts.callbacks.getProductsBySkusXrs(
      merchantProductIDs,
      // This callback will be called by commerce platform-specific implementations once data has been retrieved from
      // its respective API
      function returnProductDataCallback(productData) {
        resolveCallback(productData);
      },
      function errorCallback() {
        rejectCallback();
      }
    );
    return productDataPromise;
  },

  /**
   * Get hash of an array
   *
   * Merges all the product IDs and joins them with a separator
   *
   * @param productSkus
   * @returns {*}
   */
  getProductSkusHash: function(productSkus) {
    return productSkus.reduce(
      (accumulator, productSku) => (accumulator += `_${productSku}`)
    );
  },

  getProductsCollectionHandleFromEcommerce: function(handle, sortKey) {
    let resolveCallback,
      rejectCallback = [];

    const productDataPromise = new Promise((resolve, reject) => {
      resolveCallback = resolve;
      rejectCallback = reject;
    });

    // console.log(productDataPromise,'productDataPromise')

    window.compatioConfig.compatibleProducts.callbacks.getProductsByCollection(
      handle,
      sortKey,
      function returnProductDataCallback(productData) {
        resolveCallback(productData);
      },
      function errorCallback() {
        rejectCallback();
      }
    );
    return productDataPromise;
  },

  /**
   * Returns a formatted object of product data from commerce platform.
   *
   * @param requestedProductSkus
   * @returns {Promise<Array>}
   */
  loadProductDataFromEcommerce: function(
    requestedProductSkus,
    eCommercePlatform,
    compatioCategoryData
  ) {
    // console.log(requestedProductSkus,'requestedProductSkus')
    const productSkuHash = this.getProductSkusHash(requestedProductSkus);

    // See comment on productRetrievalPromiseCache property for rational for why we're caching the Promise
    if (this.productRetrievalPromiseCache.hasOwnProperty(productSkuHash)) {
      return this.productRetrievalPromiseCache[productSkuHash];
    }

    if (eCommercePlatform == 'shopify') {
      this.variantSKUsToProductIdMap = this.getSkuToProductMap(
        compatioCategoryData
      );
    }
    const retrieveProductsPromise = new Promise(
      async function(resolve, reject) {
        // Remove IDs that have already been cached
        let idsToload = requestedProductSkus.filter(id => {
          return !this.productCache.hasOwnProperty(id);
        });

        try {
          if (idsToload.length) {
            // Retrieve products not in cache
            const products = await this.getProductsBySkusFromEcommerce(
              idsToload,
              eCommercePlatform
            );
            // Cache newly retrieved products
            idsToload.forEach(productSku => {
              let product = products.data.find(
                product => product.merchantProductSku === productSku
              );
              // If a product is not returned from the API, store empty value in cache so we don't have to retrieve it again
              if (!product) {
                product = null;
              }
              this.productCache[productSku] = product;
            });
          }

          // Get requested products from cache
          let requestedProducts = [];
          Object.keys(this.productCache).forEach(productSku => {
            if (
              requestedProductSkus.includes(productSku) &&
              this.productCache[productSku]
            ) {
              requestedProducts.push(this.productCache[productSku]);
            }
          });
          resolve({ data: requestedProducts, options: this.variantOptions });
        } catch (e) {
          reject(e);
        }
      }.bind(this)
    );

    // Store Promise in case these exact products are requested before initial API call resolves
    this.productRetrievalPromiseCache[productSkuHash] = retrieveProductsPromise;
    return retrieveProductsPromise;
  },

  loadProductDataFromEcommerceBundler: function(
    requestedProductSkus,
    eCommercePlatform,
    compatioCategoryData
  ) {
    // console.log(requestedProductSkus,'before')

    // requestedProductSkus.sort((productA, productB) => {
    //   return productA['compatioScore'] > productB['compatioScore'] ? -1 : 1;
    // })

    requestedProductSkus = requestedProductSkus.map(e => e.merchantProductId);
    const productSkuHash = this.getProductSkusHash(requestedProductSkus);

    // See comment on productRetrievalPromiseCache property for rational for why we're caching the Promise
    if (this.productRetrievalPromiseCache.hasOwnProperty(productSkuHash)) {
      return this.productRetrievalPromiseCache[productSkuHash];
    }

    if (eCommercePlatform == 'shopify') {
      this.variantSKUsToProductIdMap = this.getSkuToProductMap(
        compatioCategoryData
      );
    }
    const retrieveProductsPromise = new Promise(
      async function(resolve, reject) {
        // Remove IDs that have already been cached
        let idsToload = requestedProductSkus.filter(id => {
          return !this.productCache.hasOwnProperty(id);
        });

        try {
          if (idsToload.length) {
            // Retrieve products not in cache
            const products = await this.getProductsBySkusFromEcommerce(
              idsToload,
              eCommercePlatform
            );

            // Cache newly retrieved products
            idsToload.forEach(productSku => {
              let product = products.data.find(
                product => product.merchantProductSku === productSku
              );
              // If a product is not returned from the API, store empty value in cache so we don't have to retrieve it again
              if (!product) {
                product = null;
              }
              this.productCache[productSku] = product;
            });
          }

          // Get requested products from cache
          let requestedProducts = [];
          Object.keys(this.productCache).forEach(productSku => {
            if (
              requestedProductSkus.includes(productSku) &&
              this.productCache[productSku]
            ) {
              requestedProducts.push(this.productCache[productSku]);
            }
          });
          requestedProducts.map(e => {
            compatioCategoryData[0].products.map(ee => {
              if (e.merchantProductSku === ee.merchantProductId) {
                e.compatioScore = ee.compatioScore;
                return e;
              }
            });
            return e;
          });
          // console.log(requestedProducts,compatioCategoryData[0].products ,'retrieveProductsPromise')
          resolve({ data: requestedProducts, options: this.variantOptions });
        } catch (e) {
          reject(e);
        }
      }.bind(this)
    );

    // Store Promise in case these exact products are requested before initial API call resolves
    this.productRetrievalPromiseCache[productSkuHash] = retrieveProductsPromise;
    return retrieveProductsPromise;
  },

  loadProductDataFromEcommerceXrs: function(
    requestedProductSkus,
    eCommercePlatform,
    compatioCategoryData
  ) {
    // console.log(requestedProductSkus,'requestedProductSkus')
    const productSkuHash = this.getProductSkusHash(requestedProductSkus);
    // See comment on productRetrievalPromiseCache property for rational for why we're caching the Promise
    if (this.productRetrievalPromiseCache.hasOwnProperty(productSkuHash)) {
      return this.productRetrievalPromiseCache[productSkuHash];
    }

    if (eCommercePlatform == 'shopify') {
      this.variantSKUsToProductIdMap = this.getSkuToProductMap(
        compatioCategoryData
      );
    }
    const retrieveProductsPromise = new Promise(
      async function(resolve, reject) {
        // Remove IDs that have already been cached
        let idsToload = requestedProductSkus.filter(id => {
          return !this.productCache.hasOwnProperty(id);
        });

        try {
          if (idsToload.length) {
            // Retrieve products not in cache
            const products = await this.getProductsBySkusFromEcommerceXrs(
              idsToload,
              eCommercePlatform
            );
            // Cache newly retrieved products
            idsToload.forEach(productSku => {
              let product = products.data.find(
                product => product.merchantProductSku === productSku
              );
              // If a product is not returned from the API, store empty value in cache so we don't have to retrieve it again
              if (!product) {
                product = null;
              }
              this.productCache[productSku] = product;
            });
          }

          // Get requested products from cache
          let requestedProducts = [];
          Object.keys(this.productCache).forEach(productSku => {
            if (
              requestedProductSkus.includes(productSku) &&
              this.productCache[productSku]
            ) {
              requestedProducts.push(this.productCache[productSku]);
            }
          });
          resolve({ data: requestedProducts, options: this.variantOptions });
        } catch (e) {
          reject(e);
        }
      }.bind(this)
    );

    // Store Promise in case these exact products are requested before initial API call resolves
    this.productRetrievalPromiseCache[productSkuHash] = retrieveProductsPromise;
    return retrieveProductsPromise;
  },

  loadCollectionDataFromECommerce: function(handle, sortKey) {
    const retrieveProductsPromise = new Promise(
      async function(resolve, reject) {
        try {
          const products = await this.getProductsCollectionHandleFromEcommerce(
            handle,
            sortKey
          );
          resolve({
            data: products.collectionData,
            productList: products.productList,
          });
          // console.log(products.collectionData,products.productList)
        } catch (e) {
          reject(e);
        }
      }.bind(this)
    );

    return retrieveProductsPromise;
  },
};

export default EcommerceApi;
